import React, { useEffect, useMemo, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { useEstados, useMunicipios } from '../../geo/hooks';
import { formatCidadeUf, parseCidadeUf } from './format';

interface CidadeUfSelectProps {
  /** Valor gravado no formato `Cidade/UF`. */
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

/**
 * UF + Cidade com as listas do IBGE (`/geo`), gravadas num único campo
 * `Cidade/UF`.
 *
 * Cadastro antigo com cidade em texto livre não é apagado: o valor fica
 * como está até o usuário escolher UF e cidade.
 */
export function CidadeUfSelect({ value, onChange, error, disabled }: CidadeUfSelectProps) {
  const parsed = parseCidadeUf(value);
  const [uf, setUf] = useState(parsed?.uf ?? '');
  const estadosQuery = useEstados();
  const municipiosQuery = useMunicipios(uf);

  // Mantém a UF em sincronia quando o valor muda de fora (ex.: hidratação).
  useEffect(() => {
    if (parsed?.uf) setUf(parsed.uf);
  }, [parsed?.uf]);

  const cidade = parsed && parsed.uf === uf ? parsed.cidade : '';
  const legado = value.trim() && !parsed ? value.trim() : null;

  const municipios = useMemo(
    () =>
      (municipiosQuery.data ?? [])
        .slice()
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [municipiosQuery.data],
  );

  return (
    <>
      <div>
        <AutocompleteSelect
          label="UF"
          options={(estadosQuery.data ?? []).map((estado) => ({
            value: estado.uf,
            label: `${estado.uf} — ${estado.nome}`,
          }))}
          value={uf}
          onChange={(next) => {
            setUf(next);
            // Trocar a UF invalida a cidade escolhida.
            if (next !== parsed?.uf) onChange(legado ?? '');
          }}
          disabled={disabled || estadosQuery.isLoading || estadosQuery.isError}
          error={error && !uf ? error : undefined}
        />
        {estadosQuery.isError && (
          <span className="mt-1 block text-xs text-red-600">
            Não foi possível carregar as UFs. Tente novamente em instantes.
          </span>
        )}
      </div>
      <div>
        <AutocompleteSelect
          label="Cidade"
          options={[
            // Nome divergente do IBGE não some da tela.
            ...(cidade && !municipios.some((municipio) => municipio.nome === cidade)
              ? [{ value: cidade, label: cidade }]
              : []),
            ...municipios.map((municipio) => ({ value: municipio.nome, label: municipio.nome })),
          ]}
          value={cidade}
          onChange={(next) => onChange(next ? formatCidadeUf(next, uf) : '')}
          disabled={disabled || !uf || municipiosQuery.isLoading}
          error={error && uf ? error : undefined}
        />
        {legado && (
          <span className="mt-1 block text-xs text-gray-500">
            Cadastrado como “{legado}”. Selecione UF e cidade para atualizar.
          </span>
        )}
      </div>
    </>
  );
}
